import { SmartLink } from "@selector/shared/components";
import { type FC, type ReactElement } from "react";

import { mapSequenceTypeToEntityType } from "@/app-utils/route.ts";
import {
  type BlastResult,
  type ExternalReference,
  type Gene,
  type GeneMutation,
  type Organism,
  type Pfam,
  type Protein,
  type ProteinMutation,
  type Region,
  type Sequence,
  type Transcript,
  type Variant,
  type VcfVariantInstance,
} from "@/graphql.ts";
import { ENTITY_TYPE } from "@/settings/settings.js";
import { type IAgGridCellProps } from "@/ui-modules/core/grid/cell-base-interfaces.ts";
import { EXTERNAL_REFERENCE_PATTERN } from "@/ui-modules/core/grid/cell-renderer-factory.tsx";
import { traverseAndCollect } from "@/ui-modules/core/grid/column-props-factory.ts";

import ListCellContent, { type ListCellItem } from "./ListCellContent.js";
import { TableCell } from "./TableCell.js";

export const createCellLink = (value: string, link: string, targetIsCurrentPage = true) => {
  if (!value) {
    return null;
  }

  if (!link) {
    return value;
  }

  const isExternal = !targetIsCurrentPage;
  return (
    <SmartLink style={{ width: "auto", display: "inline" }} to={link} forceExternal={isExternal}>
      {value}
    </SmartLink>
  );
};

export const correspondsToFieldPath = (field: string, path: string): boolean => {
  return field && (field === path || field.endsWith(`.${path}`));
};

export function createListOfValuesForSingleListCellItem<T>(
  obj: T | T[],
  getCellLink: (item: T) => any,
  getTooltip: (item: T) => any,
): ListCellItem {
  const list: T[] = Array.isArray(obj) ? obj : [obj]; // convert anyway into a list of lists
  const linkValues: ReactElement[] = list.map((item: T, idx: number) => (
    <span key={`item-${idx}`}>
      {idx > 0 ? ", " : ""}
      {getCellLink(item)}
    </span>
  ));
  const tooltips = list.map(getTooltip).join("\n");
  return {
    value: linkValues.length > 0 ? <>{linkValues}</> : null, // should be null rather than [] for the ListCellContent component logic
    tooltip: tooltips + (Array.isArray(obj) && obj.length > 1 ? "\n" : ""), // \n at the end to split the tooltips into chunks
  };
}

function generateItems(field: string, listObjects: any[]): ListCellItem[] {
  // Implements "first match wins" strategy.
  //   Put the most specific handler first.

  // We have to keep this one in front of the "genome.name" handler because it needs precedence.
  // Will beneft from some future refactoring, e.g. creating dedicated cell renderers or spliting groupField
  // into 2 distinct settings, one holding together the group's columns, one defining the root for the data multiplication.
  if (correspondsToFieldPath(field, "bestBlastResults.protein.genome.name")) {
    return listObjects.map((blastResult: BlastResult) => ({
      value: createCellLink(
        blastResult.protein.genome.name,
        `/${ENTITY_TYPE.ORGANISM}/${blastResult.protein.genome.id}`,
      ),
      tooltip: blastResult.protein.genome.name,
    }));
  }

  if (
    correspondsToFieldPath(field, "genome.name") ||
    correspondsToFieldPath(field, "organisms.name") ||
    correspondsToFieldPath(field, "components.name") ||
    correspondsToFieldPath(field, "target.virtualGenomes.name")
  ) {
    return listObjects.map((genome: Organism) => ({
      value: createCellLink(genome.name, `/${ENTITY_TYPE.ORGANISM}/${genome.id}`),
      tooltip: genome.name,
    }));
  }

  if (correspondsToFieldPath(field, "vcfVariantInstances.varGenome.name")) {
    return listObjects.map((variantInstance: VcfVariantInstance) => ({
      value: createCellLink(variantInstance.varGenome.name, `/${ENTITY_TYPE.ORGANISM}/${variantInstance.varGenome.id}`),
      tooltip: variantInstance.varGenome.name,
    }));
  }

  if (correspondsToFieldPath(field, "contig.sequenceId")) {
    return listObjects.map((region: Region) => ({
      value: createCellLink(region.contig.sequenceId, `/${ENTITY_TYPE.CONTIG}/${region.contig.id}`),
      tooltip: region.contig.sequenceId,
    }));
  }

  if (correspondsToFieldPath(field, "transcripts.sequenceId")) {
    return listObjects.map((transcript: Transcript) => ({
      value: createCellLink(transcript.sequenceId, `/${ENTITY_TYPE.TRANSCRIPT}/${transcript.id}`),
      tooltip: transcript.sequenceId,
    }));
  }

  if (correspondsToFieldPath(field, "transcripts.name")) {
    return listObjects.map((transcript: Transcript) => ({
      value: createCellLink(transcript.name, `/${ENTITY_TYPE.TRANSCRIPT}/${transcript.id}`),
      tooltip: transcript.name,
    }));
  }

  if (correspondsToFieldPath(field, "proteins.sequenceId")) {
    return listObjects.map((protein: Protein) => ({
      value: createCellLink(protein.sequenceId, `/${ENTITY_TYPE.PROTEIN}/${protein.id}`),
      tooltip: protein.sequenceId,
    }));
  }

  if (correspondsToFieldPath(field, "bestBlastResults.protein.sequenceId")) {
    return listObjects.map((blastResult: BlastResult) => ({
      value: createCellLink(blastResult.protein.sequenceId, `/${ENTITY_TYPE.PROTEIN}/${blastResult.protein.id}`),
      tooltip: blastResult.protein.sequenceId,
    }));
  }

  if (correspondsToFieldPath(field, "proteinMutations.proteinLocation.protein.sequenceId")) {
    return listObjects.map((obj) =>
      createListOfValuesForSingleListCellItem<ProteinMutation>(
        obj,
        (proteinMutation: ProteinMutation) =>
          createCellLink(
            proteinMutation.proteinLocation.protein.sequenceId,
            `/${ENTITY_TYPE.PROTEIN}/${proteinMutation.proteinLocation.protein.id}`,
          ),
        (proteinMutation: ProteinMutation) => proteinMutation.proteinLocation.protein.sequenceId,
      ),
    );
  }

  if (correspondsToFieldPath(field, "proteinMutations.name")) {
    return listObjects.map((proteinMutation: ProteinMutation) => ({
      value: createCellLink(proteinMutation.name, `/${ENTITY_TYPE.VARIANT}/${proteinMutation.variant.mutId}`),
      tooltip: proteinMutation.name,
    }));
  }

  if (
    correspondsToFieldPath(field, "genes.name") ||
    correspondsToFieldPath(field, "lowerGenes.name") ||
    correspondsToFieldPath(field, "higherGenes.name") ||
    correspondsToFieldPath(field, "relatedGenes.name")
  ) {
    return listObjects.map((gene: Gene) => ({
      value: createCellLink(gene.name, `/${ENTITY_TYPE.GENE}/${gene.id}`),
      tooltip: gene.name,
    }));
  }

  if (correspondsToFieldPath(field, "geneMutations.gene.name")) {
    return listObjects.map((geneMutation: GeneMutation) => ({
      value: createCellLink(geneMutation.gene.name, `/${ENTITY_TYPE.GENE}/${geneMutation.gene.id}`),
      tooltip: geneMutation.gene.name,
    }));
  }

  if (correspondsToFieldPath(field, "lowerVariants.name") || correspondsToFieldPath(field, "higherVariants.name")) {
    return listObjects.map((variant: Variant) => ({
      value: createCellLink(variant.name, `/${ENTITY_TYPE.VARIANT}/${variant.mutId}`),
      tooltip: variant.name,
    }));
  }

  if (correspondsToFieldPath(field, "pfamMotifs.name")) {
    return listObjects.map((pf: Pfam) => ({
      value: createCellLink(pf.name, pf.externalReference && pf.externalReference.link, false),
      tooltip: pf.name,
    }));
  }

  if (correspondsToFieldPath(field, "externalReferences.displayName")) {
    return listObjects.map((ref: ExternalReference) => ({
      value: createCellLink(ref.displayName, ref.link, false),
      tooltip: ref.displayName,
    }));
  }

  if (correspondsToFieldPath(field, "literatureReferences.displayName")) {
    return listObjects.map((ref: ExternalReference) => ({
      value: createCellLink(ref.displayName, ref.link, false),
      tooltip: ref.displayName,
    }));
  }

  if (correspondsToFieldPath(field, "internalReferences.identifier")) {
    return listObjects.map((sequence: Sequence) => ({
      value: createCellLink(
        sequence.identifier,
        `/${mapSequenceTypeToEntityType(sequence.sequenceGroup.name)}/${sequence.id}`,
      ),
      tooltip: sequence.identifier,
    }));
  }

  // dynamic fields! like externalReference122.name
  if (new RegExp(EXTERNAL_REFERENCE_PATTERN).test(field)) {
    return listObjects.map((ref: ExternalReference) => ({
      value: createCellLink(ref.name, ref.link, false),
      tooltip: ref.name,
    }));
  }
}

const LinkListCell: FC<IAgGridCellProps> = (props) => {
  if (!props.value || !props.data) {
    return null;
  }

  const { data, colDef } = props;
  const { field, groupField } = colDef;

  const listObjects: any[] = traverseAndCollect(data, groupField, true);
  //  .filter((o) => o !== null && o !== undefined); // instead of !o os that the 0 value still is valid

  // This unwinds any list structure along the path of the groupField
  // The remaining sub-path of the field must match one of the handled path in generateItems
  // and the resulting data structure in the listObject must match that sub-path!
  // Remember the handling of
  //  "proteinLocations.region.contig.accession" with "proteinLocations.region" as groupField  creates a list of "contig"
  //  while "mutVariants.region.contig.accession" with "mutVariants" as groupField creates a list of "region"
  //

  // we need to retrieve the length of the current protein, which must be done differently for plain protein and for prtoein location etc. tables
  const items = generateItems(field, listObjects);

  return (
    <TableCell>
      <ListCellContent items={items} colDef={props.colDef} context={props.context} />
    </TableCell>
  );
};

export default LinkListCell;
